/**
 * Capture a poster frame from a video File client-side (canvas grab).
 *
 * Seeks slightly past 0 — frame zero of encoded video is frequently black,
 * which is exactly the "black tiles in the curation grid" bug this exists to
 * fix. Best-effort by design: any failure (unsupported codec, zero
 * dimensions, canvas taint, timeout) resolves null and the upload proceeds
 * without a poster, never blocking it.
 */
export async function captureVideoPoster(file: File): Promise<Blob | null> {
  if (typeof document === 'undefined') return null;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
      resolve(blob);
    };
    const timer = setTimeout(() => finish(null), 8000);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => finish(null);
    video.onloadeddata = () => {
      try {
        const duration = Number.isFinite(video.duration) ? video.duration : 1;
        video.currentTime = Math.min(0.5, duration / 10);
      } catch {
        finish(null);
      }
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        if (!canvas.width || !canvas.height) {
          finish(null);
          return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(video, 0, 0);
        canvas.toBlob((blob) => finish(blob), 'image/jpeg', 0.82);
      } catch {
        finish(null);
      }
    };
    video.src = url;
  });
}
